import React, { useContext, useEffect, useMemo, useState } from "react";
import { Spin, Tooltip, Empty } from "antd";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../config/AuthContext";
import { getJavamonImage } from "../../../dao/jutge/GetJavamonConfig";
import { GetJutgeUsuariIdPoints } from "../../../dao/jutge/GetJutgeUsuariIdPoints";

/**
 * Mochila (inventario de objetos) en estilo RPG para el juego.
 * Se muestra dentro de un iframe NO a pantalla completa.
 *
 * El endpoint /jutge/usuaris/{id}/points mezcla dos cosas muy distintas:
 *   - OBJETOS reales: items comprados en la tienda (tienen `id_item`, `ball`
 *     e imagen real). Se muestran como casillas de inventario.
 *   - RECOMPENSAS: puntos ganados (doctorat.points). NO tienen `id_item` ni
 *     imagen; no son objetos, son recompensas. Se muestran en una sección
 *     aparte, más abajo, y nunca como casillas de inventario.
 * Además se muestran los puntos totales del jugador (suma de `punts`).
 */

interface Objecte {
    id: number;
    tipo: string;
    color?: string;
    punts?: number;
    descripcio?: string;
    image?: string;
    imagen?: string;
    imatge?: string;
    Imagen?: string;
    ability?: string;
    ball?: string;
    has_image?: number;
    id_item?: number | null;
}

/** Un registro es un objeto de inventario sólo si proviene de la tienda (tiene id_item). */
const isInventoryObject = (o: Objecte): boolean => o.id_item !== null && o.id_item !== undefined;

const GameMochila: React.FC = () => {
    const { isLoggedIn, userId } = useContext(AuthContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [entries, setEntries] = useState<Objecte[]>([]);

    // Solo accesible dentro del iframe del juego.
    useEffect(() => {
        if (window.self === window.top) {
            navigate("/home", { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        if (!isLoggedIn || !userId) return;
        (async () => {
            setLoading(true);
            try {
                const data = await GetJutgeUsuariIdPoints(userId + "");
                setEntries(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Error cargando la mochila:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [isLoggedIn, userId]);

    const objects = useMemo(() => entries.filter(isInventoryObject), [entries]);
    const rewards = useMemo(() => entries.filter((e) => !isInventoryObject(e)), [entries]);
    const totalPoints = useMemo(
        () => entries.reduce((sum, e) => sum + (typeof e.punts === "number" ? e.punts : 0), 0),
        [entries]
    );

    const renderTooltip = (obj: Objecte) => (
        <div style={{ maxWidth: 220 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{obj.tipo}</div>
            {obj.descripcio && <div style={{ fontSize: 12 }}>{obj.descripcio}</div>}
            {typeof obj.punts === "number" && obj.punts > 0 && (
                <div style={{ fontSize: 12, color: "#ffd54a", marginTop: 2 }}>Valor: {obj.punts}</div>
            )}
        </div>
    );

    const sectionTitle = (text: string) => (
        <div
            style={{
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: 0.5,
                color: "#f0c060",
                borderBottom: "1px solid rgba(240,192,96,0.3)",
                paddingBottom: 4,
                margin: "18px 0 10px",
            }}
        >
            {text}
        </div>
    );

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                minHeight: "100%",
                boxSizing: "border-box",
                padding: 16,
                background: "linear-gradient(180deg, #3b2a1a 0%, #2a1d12 100%)",
                color: "#f5e9d6",
                fontFamily: "'Segoe UI', sans-serif",
                overflowY: "auto",
            }}
        >
            <div style={{ textAlign: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1, textShadow: "0 2px 0 rgba(0,0,0,0.4)" }}>
                    🎒 MOCHILA
                </span>
            </div>

            {/* Puntos totales del jugador */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    margin: "0 auto 6px",
                    padding: "8px 16px",
                    maxWidth: 260,
                    borderRadius: 12,
                    background: "radial-gradient(circle at 50% 0%, #5a4426 0%, #3a2b17 100%)",
                    border: "2px solid #7a5c38",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.4)",
                }}
            >
                <span style={{ fontSize: 20 }}>⭐</span>
                <span style={{ fontSize: 13, color: "#d8c7ad" }}>Puntos totales:</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#ffd54a" }}>{totalPoints}</span>
            </div>

            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 40 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {/* Sección de objetos */}
                    {sectionTitle("Objetos")}
                    {objects.length === 0 ? (
                        <Empty
                            description={<span style={{ color: "#d8c7ad" }}>No tienes objetos en la mochila.</span>}
                            style={{ marginTop: 10, marginBottom: 10 }}
                        />
                    ) : (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                                gap: 10,
                            }}
                        >
                            {objects.map((obj, idx) => (
                                <Tooltip key={`${obj.id}-${idx}`} title={renderTooltip(obj)} placement="top">
                                    <div
                                        style={{
                                            aspectRatio: "1 / 1",
                                            borderRadius: 10,
                                            background: "radial-gradient(circle at 50% 35%, #6b5035 0%, #4a3824 100%)",
                                            border: "2px solid #7a5c38",
                                            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.4)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "transform 0.1s, border-color 0.1s",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "scale(1.06)";
                                            e.currentTarget.style.borderColor = "#f0c060";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "scale(1)";
                                            e.currentTarget.style.borderColor = "#7a5c38";
                                        }}
                                    >
                                        <img
                                            src={getJavamonImage(obj)}
                                            alt={obj.tipo}
                                            style={{ width: "78%", height: "78%", objectFit: "contain", imageRendering: "pixelated" }}
                                            onError={(e) => { (e.target as HTMLImageElement).style.visibility = "hidden"; }}
                                        />
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                    )}

                    {/* Sección de recompensas (no son objetos) */}
                    {rewards.length > 0 && (
                        <>
                            {sectionTitle("Recompensas")}
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {rewards.map((rw, idx) => (
                                    <div
                                        key={`rw-${rw.id}-${idx}`}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 10,
                                            padding: "8px 12px",
                                            borderRadius: 10,
                                            background: "rgba(255,255,255,0.04)",
                                            border: "1px solid rgba(240,192,96,0.25)",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                            <span style={{ fontSize: 18 }}>🏅</span>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 700, fontSize: 14 }}>{rw.tipo}</div>
                                                {rw.descripcio && (
                                                    <div style={{ fontSize: 12, color: "#d8c7ad" }}>{rw.descripcio}</div>
                                                )}
                                            </div>
                                        </div>
                                        {typeof rw.punts === "number" && rw.punts > 0 && (
                                            <span
                                                style={{
                                                    flexShrink: 0,
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    color: "#ffd54a",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                +{rw.punts} pts
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default GameMochila;
